// `type ComponentType` en NIET `Component`: PAGE_MAP hieronder annoteert met
// ComponentType, en dat symbool werd nergens geïmporteerd terwijl `Component`
// (de class) ongebruikt binnenkwam. Dezelfde regressie is in dit project al
// meerdere keren gerepareerd in AdminShell en in de renderers; hij compileert
// alleen zolang de tsconfig los genoeg staat.
import React, { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Menu, X, AlertCircle } from 'lucide-react';
import { TeacherSidebarV2, TeacherV2Route } from './TeacherSidebarV2';
import { VerificationBanner } from './VerificationBanner';
import { TodayPage } from './pages/TodayPage';
import { MyClassPage } from './pages/MyClassPage';
import { HomeworkPage } from './pages/HomeworkPage';
import { SubjectsPage } from './pages/SubjectsPage';
import { AssessmentsPage } from './pages/AssessmentsPage';
import { ReportsPage } from './pages/ReportsPage';
import { ManagementPage } from './pages/ManagementPage';
import { SettingsPage } from './pages/SettingsPage';
import { StudentDetailPanel, StudentData } from './pages/StudentDetailPanel';
import { useAppStore } from '../../app/hooks/useAppStore';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
import { SkeletonPage } from '../../components/shared/Skeleton';
import { isTeacherAccount, getLinkedLearners, getTeacherClassrooms, canPerform } from '../../accounts';
import type { Account, Classroom } from '../../accounts';
import { warn } from '../../utils/logger';
import { GroupId } from '../../config/systemConfig';
import { ModuleId, MODULES } from '../../content';
import { getModuleMasteryFromFamilies, V11_CONFIG } from '../../mastery';
import { hydrateLearnerProgress } from '../../data/supabase/hydrateLearnerProgress';
interface TeacherPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  onSelectStudent: (studentId: string) => void;
  activeAccount: Account;
  classrooms: Classroom[];
  selectedClassroomId: string | null;
  onSelectClassroom: (id: string | null) => void;
  onNavigate: (route: TeacherV2Route) => void;
  classroomName?: string;
  onViewDashboard?: (studentId: string) => void;
  initialTab?: 'klaarzetten' | 'resultaten';
  initialSearchQuery?: string;
  onViewAssessmentResults?: () => void;
  activeTeacherId?: string;
}
const PAGE_MAP: Record<TeacherV2Route, ComponentType<Partial<TeacherPageProps>>> = {
  today: TodayPage,
  class: MyClassPage,
  homework: HomeworkPage as ComponentType<Partial<TeacherPageProps>>,
  subjects: SubjectsPage,
  assessments: AssessmentsPage,
  reports: ReportsPage,
  management: ManagementPage,
  settings: SettingsPage as ComponentType<Partial<TeacherPageProps>>
};
const ROUTE_LABELS: Record<TeacherV2Route, string> = {
  today: 'Vandaag',
  class: 'Mijn Klas',
  homework: 'Huiswerk',
  subjects: 'Vakken',
  assessments: 'Toetsen',
  reports: 'Rapporten',
  management: 'Klas Beheer',
  settings: 'Instellingen'
};
interface TeacherShellV2Props {
  onNavigateToLearner?: (learnerId: string) => void;
  onLogout?: () => void;
}
export function TeacherShellV2({
  onNavigateToLearner,
  onLogout
}: TeacherShellV2Props) {
  const [activeRoute, setActiveRoute] = useState<TeacherV2Route>('today');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [insightStudentId, setInsightStudentId] = useState<string | null>(null);
  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(null);
  const [assessmentInitialTab, setAssessmentInitialTab] = useState<'klaarzetten' | 'resultaten' | undefined>(undefined);
  const [assessmentSearchQuery, setAssessmentSearchQuery] = useState<string | undefined>(undefined);
  const {
    accounts,
    activeAccount,
    selectedGroup,
    getLearnerData,
    getLearnerModuleMastery,
    isInitialized,
    setViewingLearner,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner
  } = useAppStore();
  const isTeacher = activeAccount && isTeacherAccount(activeAccount);

  /*
   Beide grenzen komen uit de rechtentabel en niet meer uit een eigen
   rolvergelijking. `canPerform` is de enige plek waar staat wie wat mag; stond
   het hier óók, dan kon het uiteenlopen — en dat is precies wat gebeurde
   (Klas Beheer gaf een co-docent meer dan dit scherm).
    De knoppen worden hiermee VERBORGEN. De handlers toetsen daarnaast nog een
   keer: een verborgen knop is geen grens, alleen een aanwijzing.
   */
  const canManageStudents = activeAccount ? canPerform(activeAccount, 'manage_classroom_students', {
    accounts,
    targetClassroomId: selectedClassroomId ?? undefined
  }) : false;
  const canUnlinkLearner = activeAccount ? canPerform(activeAccount, 'unlink_learner', {
    accounts,
    targetClassroomId: selectedClassroomId ?? undefined
  }) : false;
  const classrooms = isTeacher ? getTeacherClassrooms(accounts, activeAccount.id) : [];
  useEffect(() => {
    if (selectedClassroomId && !classrooms.find(c => c.id === selectedClassroomId)) {
      setSelectedClassroomId(null);
    }
    // Auto-select the only classroom if none is selected
    if (!selectedClassroomId && classrooms.length === 1) {
      setSelectedClassroomId(classrooms[0].id);
    }
  }, [selectedClassroomId, classrooms]);
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const activeGroupLevel = (activeClassroom?.groupLevel || selectedGroup) as GroupId;
  const allLinkedLearners = isTeacher ? getLinkedLearners(accounts, activeAccount.id) : [];
  const displayLearners = activeClassroom ? allLinkedLearners.filter(l => activeClassroom.learnerIds.includes(l.id)) : allLinkedLearners;

  /*
   ── De voortgang van de klas ophalen ────────────────────────────────────
    Dit portaal las voortgang UITSLUITEND uit de lokale store. Op het apparaat
   van een leerkracht heeft geen enkele leerling ooit geoefend, dus stond daar
   niets — en de schermen presenteerden die leegte als een meting:
      "Gemiddelde beheersing 0%", "Zwakste vak … 0% beheersing",
     "Niet geoefend deze week" voor de hele klas, en Djay die op grond
     daarvan een oordeel over een kind uitspreekt.
    Dat is de ernstigste variant van dit terugkerende patroon, omdat een
   leerkracht op die getallen HANDELT: hij zet extra werk klaar, of belt een
   ouder over een kind dat thuis prima oefent.
    `can_view_learner()` (0016) staat deze leesactie expliciet toe — de
   serverkant was dus al klaar; alleen de aanroep ontbrak. Het ouderportaal
   deed dit al, dit portaal niet.
    `hydrateLearnerProgress` is bewust dezelfde functie die het ouderportaal
   gebruikt: één definitie van "wat komt van de server".
   */
  const rosterIds = useMemo(() => displayLearners.map(l => l.id), [displayLearners]);
  const rosterKey = rosterIds.join(',');
  const [progressUnavailable, setProgressUnavailable] = useState(false);
  useEffect(() => {
    if (!isTeacher || rosterIds.length === 0) return;
    let cancelled = false;
    void hydrateLearnerProgress(rosterIds).then(ok => {
      if (!cancelled) setProgressUnavailable(!ok);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher, rosterKey]);
  const students: StudentData[] = useMemo(() => {
    return displayLearners.map(learner => {
      const learnerData = getLearnerData(learner.id);
      if (!learnerData) {
        warn(`No data found for student ${learner.id}`);
        return null;
      }
      return {
        id: learner.id,
        name: learner.displayName,
        data: learnerData
      };
    }).filter((s): s is StudentData => s !== null);
  }, [displayLearners, getLearnerData]);
  const attentionCount = useMemo(() => {
    let count = 0;
    students.forEach(student => {
      let lowestMastery = 100;
      let hasPracticed = false;
      const data = getLearnerData(student.id);
      if (!data) return;
      MODULES.forEach(module => {
        const mastery = getModuleMasteryFromFamilies(data.familyMastery || {}, module.id, activeGroupLevel);
        if (mastery > 0) {
          hasPracticed = true;
          if (mastery < lowestMastery) lowestMastery = mastery;
        }
      });
      if (hasPracticed && lowestMastery < V11_CONFIG.STRUGGLING_THRESHOLD) count++;
    });
    return count;
  }, [students, getLearnerData, activeGroupLevel]);
  // Fix: use activeGroupLevel instead of global selectedGroup for mastery lookups
  // The global selectedGroup may not match the classroom's groupLevel, causing
  // mastery keys to look up the wrong group scope (e.g. _g6 instead of _g7)
  const getModuleMasteryForClassroom = useMemo(() => {
    return (learnerId: string, moduleId: ModuleId): number => {
      const data = getLearnerData(learnerId);
      if (!data) return 0;
      return getModuleMasteryFromFamilies(data.familyMastery || {}, moduleId, activeGroupLevel);
    };
  }, [getLearnerData, activeGroupLevel]);
  const studentIds = useMemo(() => students.map(s => s.id), [students]);
  useEffect(() => {
    if (insightStudentId && !studentIds.includes(insightStudentId)) {
      setInsightStudentId(null);
    }
  }, [insightStudentId, studentIds]);
  // Clear assessment navigation state when leaving assessments route
  useEffect(() => {
    if (activeRoute !== 'assessments') {
      setAssessmentInitialTab(undefined);
      setAssessmentSearchQuery(undefined);
    }
  }, [activeRoute]);
  if (!isInitialized) {
    return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-11018" data-mp="1yr7a3w0akft6">
        <div className="hidden md:block w-64 bg-white border-r border-slate-200 shrink-0" data-id="element-11019" data-mp="23qtnc5g4dw1z" />
        <main className="flex-1 min-w-0 overflow-auto" data-id="element-11020" data-mp="27soo98u527f8">
          <SkeletonPage data-id="element-11021" data-mp="27s5c68r66d94" />
        </main>
      </div>;
  }
  if (!isTeacher) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4" data-id="element-11022" data-mp="9en72s8dcp2t">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-sm border border-slate-200 text-center" data-id="element-11023" data-mp="dfyq2ewqmc21">
          <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-11024" data-mp="18x1i9wstpzah">
            Geen toegang
          </h2>
          {/*
           Verwees naar een "account switcher" die niet meer bestaat, en bood
           daaronder een knop die naar het account van een willekeurig kind
           op dit apparaat wisselde. Beide zijn weg; zie PortalSwitch.tsx.
           */}
          <p className="text-slate-600 mb-8" data-id="element-11025" data-mp="1ruumr3j3ih9z">
            Je bent niet ingelogd als docent. Log uit en meld je opnieuw aan
            met je docentaccount.
          </p>
        </div>
      </div>;
  }
  const handleSelectStudent = (studentId: string) => {
    setInsightStudentId(studentId);
  };
  const handleCloseInsight = () => {
    setInsightStudentId(null);
  };
  const handleViewDashboard = (studentId: string) => {
    setViewingLearner(studentId);
    if (onNavigateToLearner) {
      onNavigateToLearner(studentId);
    }
  };
  const handleNavigateToAssessments = (studentName: string) => {
    setAssessmentInitialTab('resultaten');
    setAssessmentSearchQuery(studentName);
    setActiveRoute('assessments');
    setInsightStudentId(null);
  };
  const handleViewAssessmentResults = () => {
    setAssessmentInitialTab('resultaten');
    setAssessmentSearchQuery(undefined);
    setActiveRoute('assessments');
  };
  /*
   Dezelfde handeling stond op twee verschillende regels: hier gold `isOwner`,
   terwijl Klas Beheer de knop óók aan een co-docent gaf — en de onderliggende
   store-functie staat het toe voor elke rol in de klas. Een co-docent kon een
   leerling dus wél uit de klas halen via het ene scherm en niet via het
   andere, wat leest als een bug in het scherm waar het niet mag.
    De geschreven regel (`permissions.ts`, `manage_classroom_students`) zegt
   "eigenaar of co-docent", en dat is ook het gedrag dat past bij waarvoor een
   co-docent bestaat: dagelijkse begeleiding. Dus die regel volgen, en niet de
   strengere gok die hier per ongeluk stond.
    Let op het verschil met ONTKOPPELEN hieronder: dat verbreekt de band tussen
   leerkracht en leerling volledig en blijft eigenaar-alleen.
   */
  const handleRemoveFromClassroom = (studentId: string) => {
    if (!activeAccount || !selectedClassroomId) return;
    // Via de rechtentabel in plaats van een eigen rolvergelijking. Dat die
    // beslissing op twee plekken stond, is precies waarom ze uiteenliep.
    if (!canPerform(activeAccount, 'manage_classroom_students', {
      accounts,
      targetClassroomId: selectedClassroomId
    })) return;
    removeLearnerFromClassroom(activeAccount.id, selectedClassroomId, studentId);
    handleCloseInsight();
  };
  const handleUnlinkStudent = (studentId: string) => {
    if (!activeAccount) return;
    // ONTKOPPELEN is iets anders dan uit de klas halen: het verbreekt de band
    // volledig, ook buiten deze klas. `unlink_learner` staat in de tabel als
    // eigenaar-alleen, en dat blijft zo.
    if (!canPerform(activeAccount, 'unlink_learner', {
      accounts,
      targetClassroomId: selectedClassroomId ?? undefined
    })) return;
    unlinkTeacherFromLearner(activeAccount.id, studentId);
    handleCloseInsight();
  };
  const insightStudent = insightStudentId ? students.find(s => s.id === insightStudentId) : null;
  const PageComponent = PAGE_MAP[activeRoute] ?? TodayPage;

  // Alleen een EXPLICIETE pending/rejected toont de banner. Een leerkracht die
  // lokaal werkt heeft geen serverstatus, en `undefined` betekent daar
  // "onbekend" — die een waarschuwing tonen zou een probleem verzinnen dat er
  // niet is. Zelfde afweging als `isVerifiedTeacher` in permissions.ts.
  const teacherStatus = (activeAccount as {
    teacherStatus?: 'pending' | 'verified' | 'rejected';
  }).teacherStatus;
  const verificationStatus = teacherStatus === 'pending' || teacherStatus === 'rejected' ? teacherStatus : null;
  const teacherSchoolName = (activeAccount as {
    schoolName?: string;
  }).schoolName;
  // Uit het register gekozen, of zelf aangevraagd (0041)? Dat bepaalt op wie
  // deze leerkracht wacht, en dus wat de banner hem hoort te vertellen.
  const teacherSchoolFromRegistry = Boolean((activeAccount as {
    schoolFromRegistry?: boolean;
  }).schoolFromRegistry);
  return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-11026" data-mp="3gepzqbiue8o3">
      {/* Skip to content — a11y */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-11027" data-mp="3kdfkwqa4guia">
        
        Ga naar inhoud
      </a>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-slate-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-11028" data-mp="2wo4jd6ky6w33">
        
        <div className="font-bold text-slate-900 flex items-center gap-3 py-3" data-id="element-11029" data-mp="28zblh4krme5u">
          <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center border border-indigo-100" data-id="element-11030" data-mp="21hdm3swasjgc">
            <span className="text-indigo-600 font-black text-sm" data-id="element-11031" data-mp="1witzdq9vr3mx">M</span>
          </div>
          <div className="flex flex-col" data-id="element-11032" data-mp="1sfe4aydglbcc">
            <span className="text-sm font-black leading-tight" data-id="element-11033" data-mp="1sia6iywcam6l">
              {ROUTE_LABELS[activeRoute]}
            </span>
            {activeClassroom && <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-tight" data-id="element-11034" data-mp="1iij6yisx9gzn">
                {activeClassroom.name}
              </span>}
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 -mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-11035" data-mp="18roo7oevwziy">
          
          {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-11036" data-mp="urvpb5t7fczw" /> : <Menu className="w-6 h-6" data-id="element-11037" data-mp="utqb0mcav951" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-11038" data-mp="l40777hd1ayy" />
      

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform shadow-xl md:shadow-none
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-11039" data-mp="g4x1rlxgx2qg">
        
        <TeacherSidebarV2 activeRoute={activeRoute} onNavigate={route => {
        setActiveRoute(route);
        setMobileMenuOpen(false);
      }} onLogout={onLogout} classroomName={activeClassroom?.name || 'Geen klas'} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={setSelectedClassroomId} todayAttentionCount={attentionCount} activeTeacherId={activeAccount.id} data-id="element-11040" data-mp="u3n7tr29mcqy" />
        
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 overflow-auto pt-16 md:pt-0 relative" data-id="element-11041" data-mp="37b63shnvi09z">

        {/*
         Boven de inhoud en binnen de scroll: dit is de verklaring voor alles
         wat eronder leeg blijft, dus hij hoort bij die inhoud te staan en niet
         als losse mededeling in de chrome.
         */}
        {verificationStatus && <VerificationBanner status={verificationStatus} schoolName={teacherSchoolName} schoolFromRegistry={teacherSchoolFromRegistry} data-id="element-11042" data-mp="2te9cyg7r8it3" />}

        {/*
         Zonder deze melding is een mislukte ophaalpoging niet te onderscheiden
         van een klas die niets doet: elk cijfer eronder staat dan op 0% en
         iedereen staat onder "niet geoefend". Dat leest als een bevinding over
         de kinderen, en een leerkracht handelt daarop.
          Eén regel boven de inhoud in plaats van een waarschuwing per getal:
         de oorzaak is één ding, en hem bij elk cijfer herhalen maakt het
         scherm onleesbaar terwijl de boodschap dezelfde is.
         */}
        {progressUnavailable && rosterIds.length > 0 && <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" role="status" data-id="element-11043" data-mp="2h2gqdr4h3qzr">
            <p className="text-sm text-amber-900 leading-relaxed" data-id="element-11044" data-mp="2ltf88v540x54">
              <span className="font-bold" data-id="element-11045" data-mp="2luqc0uyk24w8">
                De oefengegevens zijn niet opgehaald.
              </span>{' '}
              De cijfers hieronder zijn daarom niet actueel — 0% betekent hier
              niet dat er niet geoefend is. Ververs de pagina om het opnieuw te
              proberen.
            </p>
          </div>}

        <BaseErrorBoundary logPrefix="Teacher Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-11047" data-mp="15fytrav4ie0n">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4" data-id="element-11048" data-mp="1aejk8ikkpjbw">
                <AlertCircle className="w-8 h-8 text-indigo-600" data-id="element-11049" data-mp="3npkwsv22lnzk" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2" data-id="element-11050" data-mp="3iwi38mxamejd">
                Dit scherm kon niet worden geladen
              </h2>
              {/*
               Hier stond `error.message` — de ontwikkelaarsmelding, vaak in
               het Engels en soms met een stacktrace-fragment erin. Een
               leerkracht die tussen twee lessen door iets opzoekt, kan daar
               niets mee en schrikt er hooguit van. Erger: die tekst kan
               interne namen prijsgeven aan iedereen die meekijkt op het
               digibord.
                Wat iemand op dat moment nodig heeft is: is mijn werk weg, en
               wat kan ik nu doen. Die twee vragen beantwoorden we hier; de
               technische oorzaak gaat naar de log, waar hij thuishoort.
               */
        }
              <p className="text-sm text-slate-500 mb-1 max-w-md" data-id="element-11051" data-mp="2h6czj2bnzbdy">
                Je gegevens zijn veilig — er is niets verloren gegaan.
              </p>
              <p className="text-sm text-slate-500 mb-4 max-w-md" data-id="element-11052" data-mp="2o4mcnj9qaljw">
                Probeer het opnieuw. Blijft dit gebeuren, ga dan naar een ander
                tabblad en kom hier terug.
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-11053" data-mp="2e6opz07iowte">
              
                Probeer opnieuw
              </button>
            </div>} data-id="element-11046" data-mp="34sb0w21bl3gu">
          
          <PageComponent students={students} selectedGroup={activeGroupLevel} getModuleMastery={getModuleMasteryForClassroom} onSelectStudent={handleSelectStudent} activeAccount={activeAccount} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={setSelectedClassroomId} onNavigate={setActiveRoute} classroomName={activeClassroom?.name} onViewDashboard={handleViewDashboard} initialTab={assessmentInitialTab} initialSearchQuery={assessmentSearchQuery} onViewAssessmentResults={handleViewAssessmentResults} activeTeacherId={activeAccount.id} data-id="element-11054" data-mp="36srcwgr6fody" />
          
        </BaseErrorBoundary>
      </main>

      {/* Slide-over Panel for Student Details */}
      {insightStudent && <StudentDetailPanel student={insightStudent} selectedGroup={activeGroupLevel} getModuleMastery={getModuleMasteryForClassroom} onClose={handleCloseInsight} onViewDashboard={handleViewDashboard} isOpen={!!insightStudent} classroomName={activeClassroom?.name} onRemoveFromClassroom={canManageStudents ? handleRemoveFromClassroom : undefined} onUnlinkStudent={canUnlinkLearner ? handleUnlinkStudent : undefined} onNavigateToAssessments={handleNavigateToAssessments} data-id="element-11055" data-mp="avjtei41rmpl" />}
    </div>;
}